import { useState } from 'react';
import { Plus, GripVertical, ChevronRight, Save, Trash2, X, AlertCircle } from 'lucide-react';
import { getIconComponent, AVAILABLE_ICONS, DEFAULT_COLORS } from '../../lib/icons';
import { NavigationBar } from '../../components/ui/NavigationBar';
import defaultData from '../../data/defaultExpenseTypes.json';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { useTranslations } from '../../i18n';

type ExpenseTypeData = {
    icon: string;
    color: string;
    sort_order: number;
    is_default?: boolean;
    translations: Record<string, string>;
};

type DefaultExpenseTypes = Record<string, ExpenseTypeData>;

interface DefaultExpenseTypesEditorProps {
    onBack: () => void;
}

export function DefaultExpenseTypesEditor({ onBack }: DefaultExpenseTypesEditorProps) {
    const [data, setData] = useState<DefaultExpenseTypes>(defaultData as DefaultExpenseTypes);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { t } = useTranslations();

    // Editor State
    const [editId, setEditId] = useState('');
    const [editIcon, setEditIcon] = useState('');
    const [editColor, setEditColor] = useState('');
    const [editOrder, setEditOrder] = useState(0);
    const [isDefault, setIsDefault] = useState(true);
    const [editTranslations, setEditTranslations] = useState<Record<string, string>>({});

    const sortedKeys = Object.keys(data).sort((a, b) => (data[a].sort_order || 0) - (data[b].sort_order || 0));

    const handleEditClick = (id: string, isNew = false) => {
        setEditingId(id);
        setEditId(id);
        if (isNew) {
            setEditIcon('circle');
            setEditColor(DEFAULT_COLORS[0]);
            setEditOrder((Math.max(...Object.values(data).map(d => d.sort_order || 0)) || 0) + 10);
            setEditTranslations({ en: '', lv: '', ru: '' });
            setIsDefault(true);
        } else {
            const item = data[id];
            setEditIcon(item.icon);
            setEditColor(item.color);
            setEditOrder(item.sort_order);
            setIsDefault(item.is_default !== false);
            setEditTranslations({ ...item.translations });
        }
    };

    const handleSave = () => {
        if (!editId) return;

        // If ID changed (renamed), delete old key
        const newData = { ...data };
        if (editingId && editingId !== 'new' && editingId !== editId) {
            delete newData[editingId];
        }

        // Clean translations
        const cleanTrans: Record<string, string> = {};
        Object.entries(editTranslations).forEach(([k, v]) => {
            if (v && v.trim()) cleanTrans[k] = v.trim();
        });

        newData[editId] = {
            icon: editIcon,
            color: editColor,
            sort_order: editOrder,
            is_default: isDefault,
            translations: cleanTrans
        };

        setData(newData);
        setEditingId(null);
    };

    const handleDelete = () => {
        if (!editingId || editingId === 'new') {
            setEditingId(null);
            return;
        }
        setShowDeleteModal(true);
    };

    // DnD Handlers
    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDraggedItem(id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedItem || draggedItem === targetId) return;

        const sourceIndex = sortedKeys.indexOf(draggedItem);
        const targetIndex = sortedKeys.indexOf(targetId);

        if (sourceIndex === -1 || targetIndex === -1) return;

        // We don't reorder state immediately for smooth animation efficiently, 
        // but here we can just do rudimentary swap for visual feedback if we wanted.
        // However, updating sort_order is the real goal.
        // Let's just allow drop.
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedItem || draggedItem === targetId) return;

        const sourceIndex = sortedKeys.indexOf(draggedItem);
        const targetIndex = sortedKeys.indexOf(targetId);

        const newKeys = [...sortedKeys];
        newKeys.splice(sourceIndex, 1);
        newKeys.splice(targetIndex, 0, draggedItem);

        // Update sort orders based on new index
        const newData = { ...data };
        newKeys.forEach((key, index) => {
            if (newData[key]) {
                newData[key].sort_order = (index + 1) * 10;
            }
        });

        setData(newData);
        setDraggedItem(null);
    };

    return (
        <>
        <div className="fixed inset-0 flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden font-primary z-50">
            <NavigationBar
                title="Edit Default Types (JSON)"
                fixed={false}
                onBack={onBack}
                rightAction={{
                    icon: Plus,
                    onClick: () => handleEditClick('new', true)
                }}
            />

            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                {/* List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2 border-r border-gray-200 dark:border-white/10">
                    {sortedKeys.map(key => {
                        const item = data[key];
                        const Icon = getIconComponent(item.icon);
                        const isDragging = draggedItem === key;

                        return (
                            <div
                                key={key}
                                draggable
                                onDragStart={(e) => handleDragStart(e, key)}
                                onDragOver={(e) => handleDragOver(e, key)}
                                onDrop={(e) => handleDrop(e, key)}
                                className={`flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-white/10 ${isDragging ? 'opacity-50' : ''}`}
                            >
                                <div className="cursor-grab text-gray-400">
                                    <GripVertical size={20} />
                                </div>
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                                    style={{ backgroundColor: item.color }}
                                >
                                    <Icon size={20} />
                                </div>
                                <div className="flex-1" onClick={() => handleEditClick(key)}>
                                    <div className="flex items-center gap-2">
                                        <div className="font-bold text-gray-900 dark:text-white">{item.translations['en'] || key}</div>
                                        {item.is_default !== false && (
                                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">Default</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono">{key}</div>
                                </div>
                                <button onClick={() => handleEditClick(key)} className="p-2 text-blue-500">
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        );
                    })}

                    {/* JSON Output Preview */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-gray-500 uppercase">JSON Output (Copy to file)</h3>
                            <button
                                onClick={() => navigator.clipboard.writeText(JSON.stringify(data, null, 4))}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                            >
                                Copy JSON
                            </button>
                        </div>
                        <textarea
                            className="w-full h-64 font-mono text-xs p-4 bg-slate-950 text-emerald-400 rounded-xl border border-slate-800"
                            value={JSON.stringify(data, null, 4)}
                            readOnly
                        />
                    </div>
                </div>

                {/* Editor Modal/Panel */}
                {editingId && (
                    <div className="absolute inset-0 md:static md:w-[400px] bg-white dark:bg-slate-900 z-20 flex flex-col border-l border-gray-200 dark:border-white/10 animate-in slide-in-from-right duration-200 shadow-2xl">
                        <div className="p-4 border-b border-gray-100 dark:border-white/10 flex items-center justify-between bg-white dark:bg-slate-900">
                            <h3 className="font-bold text-lg dark:text-white">
                                {editingId === 'new' ? 'New Type' : 'Edit Type'}
                            </h3>
                            <button onClick={() => setEditingId(null)} className="p-2 hover:bg-gray-100 rounded-full dark:text-white">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="bg-gray-50 dark:bg-slate-900/50 py-6 border-b border-gray-100 dark:border-white/10 flex flex-col items-center justify-center">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
                                style={{ backgroundColor: editColor }}
                            >
                                {(() => {
                                    const PreviewIcon = getIconComponent(editIcon);
                                    return <PreviewIcon size={32} className="text-white" />;
                                })()}
                            </div>
                            <p className="mt-3 text-sm font-bold text-gray-900 dark:text-white">
                                {editTranslations['en'] || 'Untitled Type'}
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Key (ID)</label>
                                <input
                                    value={editId}
                                    onChange={(e) => setEditId(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:text-white dark:border-white/10 font-mono"
                                    placeholder="fuel"
                                />
                                {editingId !== 'new' && editingId !== editId && (
                                    <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                                        <AlertCircle size={12} /> Key change will rename entry
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Color</label>
                                <div className="flex flex-wrap gap-2">
                                    {DEFAULT_COLORS.map(c => (
                                        <button
                                            key={c}
                                            onClick={() => setEditColor(c)}
                                            className={`w-8 h-8 rounded-full ${editColor === c ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                                <input
                                    value={editColor}
                                    onChange={(e) => setEditColor(e.target.value)}
                                    className="w-full mt-2 px-3 py-2 border rounded-lg text-sm dark:bg-slate-800 dark:text-white dark:border-white/10"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Icon</label>
                                <div className="grid grid-cols-6 gap-2 h-32 overflow-y-auto p-2 border rounded-lg bg-gray-50 dark:bg-slate-800/50">
                                    {AVAILABLE_ICONS.map(icon => {
                                        const Icon = getIconComponent(icon);
                                        const isSelected = editIcon === icon;
                                        return (
                                            <button
                                                key={icon}
                                                onClick={() => setEditIcon(icon)}
                                                className={`p-2 rounded flex items-center justify-center transition-all ${isSelected ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-800' : 'hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-500'}`}
                                            >
                                                <Icon size={20} />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isDefault}
                                        onChange={(e) => setIsDefault(e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                    />
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Is Default Type</span>
                                </label>
                                <p className="text-xs text-gray-500 ml-6">If checked, this type will be added to new cars by default.</p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Sort Order</label>
                                <input
                                    type="number"
                                    value={editOrder}
                                    onChange={(e) => setEditOrder(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:text-white dark:border-white/10"
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-gray-500 uppercase border-b pb-1">Translations</label>
                                {['en', 'lv', 'ru', 'lt', 'et', 'es', 'pt', 'pl', 'no', 'sv', 'de', 'fr', 'it', 'nl'].map(lang => (
                                    <div key={lang} className="flex items-center gap-2">
                                        <span className="w-8 text-xs font-bold uppercase text-gray-400">{lang}</span>
                                        <input
                                            value={editTranslations[lang] || ''}
                                            onChange={(e) => setEditTranslations(prev => ({ ...prev, [lang]: e.target.value }))}
                                            className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-slate-800 dark:text-white dark:border-white/10"
                                            placeholder={`Translation for ${lang}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-slate-900 flex gap-3">
                            {editingId !== 'new' && (
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                >
                                    <Trash2 size={20} />
                                </button>
                            )}
                            <button
                                onClick={handleSave}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
                            >
                                <Save size={20} />
                                <span>Save Changes</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        <ConfirmModal
            isOpen={showDeleteModal}
            title={t.expenses?.deleteExpense || 'Delete type'}
            message={t.expenses?.deleteConfirm || `Are you sure you want to delete "${editingId}"?`}
            confirmLabel={t.common?.delete || 'Delete'}
            cancelLabel={t.common?.cancel || 'Cancel'}
            onConfirm={() => {
                if (!editingId || editingId === 'new') {
                    setShowDeleteModal(false);
                    setEditingId(null);
                    return;
                }
                const newData = { ...data };
                delete newData[editingId];
                setData(newData);
                setEditingId(null);
                setShowDeleteModal(false);
            }}
            onCancel={() => setShowDeleteModal(false)}
            onClose={() => setShowDeleteModal(false)}
            isDestructive={true}
        />
        </>
    );
}
